// frontend/src/components/common/TimeInput.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedInput, ThemedSelect } from '../themed';
import {
  minutesToDisplayUnit,
  displayUnitToMinutes,
  getApplicableUnits,
  formatMinutesRange,
  getUnitMultiplier
} from '../../utils/timeUtils';

/**
 * Smart time input component with automatic unit selection
 * Allows users to input time in minutes, hours, or days
 * Stores value in minutes (normalized for database)
 */
const TimeInput = ({
  value, // Time in minutes
  onChange, // Callback with minutes value
  minMinutes = 1,
  maxMinutes = 10080,
  placeholder,
  className = '',
  disabled = false
}) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();

  // Convert initial minutes to display unit
  const initialDisplay = minutesToDisplayUnit(value);
  const [displayValue, setDisplayValue] = useState(initialDisplay.value);
  const [unit, setUnit] = useState(initialDisplay.unit);

  // Get applicable units based on range
  const applicableUnits = getApplicableUnits(minMinutes, maxMinutes);

  // Update display when external value changes
  useEffect(() => {
    if (value !== displayUnitToMinutes(displayValue, unit)) {
      const newDisplay = minutesToDisplayUnit(value);
      setDisplayValue(newDisplay.value);
      setUnit(newDisplay.unit);
    }
  }, [value]);

  /**
   * Handle value change
   */
  const handleValueChange = (e) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);

    // Convert to minutes and call onChange
    const minutes = displayUnitToMinutes(newValue, unit);
    console.log(`TimeInput: Value changed to ${newValue} ${unit} = ${minutes} minutes`);
    onChange(minutes);
  };

  /**
   * Handle unit change
   */
  const handleUnitChange = (e) => {
    const newUnit = e.target.value;

    // Get current total minutes
    const currentMinutes = displayUnitToMinutes(displayValue, unit);

    // Set new unit
    setUnit(newUnit);

    // Calculate new display value in the new unit
    const multiplier = getUnitMultiplier(newUnit);
    const newDisplayValue = Math.round(currentMinutes / multiplier);

    setDisplayValue(newDisplayValue);
    console.log(`TimeInput: Unit changed from ${unit} to ${newUnit}, keeping ${currentMinutes} minutes (display: ${newDisplayValue})`);

    // Keep the same minutes value (already calculated)
    onChange(currentMinutes);
  };

  // Format range display
  const rangeDisplay = formatMinutesRange(minMinutes, maxMinutes, t);

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <ThemedInput
        type="number"
        value={displayValue}
        onChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-20"
        min={1}
      />

      <ThemedSelect
        value={unit}
        onChange={handleUnitChange}
        disabled={disabled}
        className="w-28"
      >
        {applicableUnits.includes('minutes') && (
          <option value="minutes">{t('minutes')}</option>
        )}
        {applicableUnits.includes('hours') && (
          <option value="hours">{t('hours')}</option>
        )}
        {applicableUnits.includes('days') && (
          <option value="days">{t('days')}</option>
        )}
      </ThemedSelect>

      <span className={cn("text-sm whitespace-nowrap", text.secondary)}>
        ({rangeDisplay})
      </span>
    </div>
  );
};

export default TimeInput;
